import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useAppContext } from "../../../context/AppContext";
import { ScheduleTrans, ToasterTrans } from "../../../utils/translations";
import { getCourse, pollCourseUnlock } from "../../../services/courseService";

const copy = {
  english: {
    loading: "Loading course...",
    error: "Failed to load course.",
    noClasses: "No classes are available in this course yet.",
    openClass: "Open class",
    lockedBanner: "This course is locked. Studio and Online Club members have free access; others can purchase it from any class page.",
    stillProcessingTitle: "Still confirming your payment",
    stillProcessingBody: "This can take a minute. If the course hasn't unlocked yet, check your email for a receipt, or refresh to check again.",
    refresh: "Refresh",
    checking: "Checking...",
  },
  russian: {
    loading: "Загружаем курс...",
    error: "Не удалось загрузить курс.",
    noClasses: "В этом курсе пока нет доступных занятий.",
    openClass: "Открыть занятие",
    lockedBanner: "Этот курс закрыт. Участники Studio и Online Club имеют бесплатный доступ, остальные могут купить его на странице любого занятия.",
    stillProcessingTitle: "Всё ещё подтверждаем оплату",
    stillProcessingBody: "Обычно это занимает не больше минуты. Если курс всё ещё не открылся, проверьте почту — там должен быть чек, или нажмите «Обновить», чтобы проверить ещё раз.",
    refresh: "Обновить",
    checking: "Проверяем...",
  },
};

export default function CourseDetail() {
  const { courseSlug } = useParams();
  const { language, token, setToaster } = useAppContext();
  const t = copy[language] || copy.english;

  const [course, setCourse] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [pendingConfirmation, setPendingConfirmation] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let isMounted = true;

    const paymentSuccess = new URLSearchParams(window.location.search).get("payment") === "success";
    if (paymentSuccess) {
      const url = new URL(window.location.href);
      url.searchParams.delete("payment");
      window.history.replaceState({}, "", url.pathname + url.search);
      setProcessingPayment(true);
    }

    const loadCourse = async () => {
      setIsLoading(true);
      setError("");

      try {
        const data = paymentSuccess
          ? await pollCourseUnlock(courseSlug, token)
          : await getCourse(courseSlug, token);
        const sortedClasses = Array.isArray(data.classes)
          ? [...data.classes].sort((a, b) => (a.order || 0) - (b.order || 0))
          : [];

        if (isMounted) {
          setCourse({ ...data, classes: sortedClasses });
          if (paymentSuccess) {
            setPendingConfirmation(!!data.locked);
            if (!data.locked) {
              setToaster({ message: ToasterTrans[language].coursePurchased, type: "success", show: true });
            }
          }
        }
      } catch (err) {
        if (isMounted) {
          console.error(err);
          setError(t.error);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
          setProcessingPayment(false);
        }
      }
    };

    loadCourse();
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseSlug, t.error, token]);

  const handleRefreshAccess = async () => {
    setRefreshing(true);
    try {
      const data = await getCourse(courseSlug, token);
      setCourse((prev) => ({ ...prev, ...data }));
      if (!data.locked) {
        setPendingConfirmation(false);
        setToaster({ message: ToasterTrans[language].coursePurchased, type: "success", show: true });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <main className="min-h-screen mt-10 px-5 py-8 lg:px-16">
      <div className="mx-auto w-full max-w-5xl">
        {isLoading && (
          <p className="text-center">
            {processingPayment ? ScheduleTrans[language].processingPayment : t.loading}
          </p>
        )}
        {!isLoading && error && <p className="text-center text-red-600">{error}</p>}

        {!isLoading && !error && course && (
          <>
            <h1 className="text-3xl font-semibold">{course.title}</h1>
            {course.description && <p className="mt-3 text-gray-700">{course.description}</p>}
            {course.locked && pendingConfirmation && (
              <div className="mt-3 rounded-md bg-amber-50 px-4 py-3 text-sm text-amber-800">
                <p className="font-semibold">{t.stillProcessingTitle}</p>
                <p className="mt-1">{t.stillProcessingBody}</p>
                <button
                  type="button"
                  onClick={handleRefreshAccess}
                  disabled={refreshing}
                  className="mt-3 rounded-md border border-amber-800/40 px-4 py-2 text-sm font-medium text-amber-800 disabled:opacity-50"
                >
                  {refreshing ? t.checking : t.refresh}
                </button>
              </div>
            )}
            {course.locked && !pendingConfirmation && (
              <p className="mt-3 rounded-md bg-amber-50 px-4 py-3 text-sm text-amber-800">
                {t.lockedBanner}
                {course.nisPrice > 0 && (
                  <span className="ml-1 font-semibold">
                    ({course.nisPrice} ₪{course.rubPrice > 0 ? ` / ${course.rubPrice} ₽` : ""})
                  </span>
                )}
              </p>
            )}
            {course.thumbnail && (
              <img
                src={course.thumbnail}
                alt={course.title}
                className="mt-6 h-64 w-full rounded-lg object-cover"
              />
            )}

            <section className="mt-8 space-y-4">
              {course.classes?.length ? (
                course.classes.map((courseClass) => (
                  <article
                    key={courseClass.slug}
                    className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm"
                  >
                    <h2 className="text-xl font-semibold">{courseClass.title}</h2>
                    {courseClass.summary && (
                      <p className="mt-2 text-sm text-gray-600">{courseClass.summary}</p>
                    )}
                    <a
                      href={`/courses/${course.slug}/classes/${courseClass.slug}`}
                      className="mt-4 inline-flex rounded-md bg-brown px-4 py-2 text-sm text-white"
                    >
                      {t.openClass}
                    </a>
                  </article>
                ))
              ) : (
                <p className="text-gray-600">{t.noClasses}</p>
              )}
            </section>
          </>
        )}
      </div>
    </main>
  );
}
